import Card from 'components/card';
import Pagination from 'components/pagination/Pagination';
import { useEffect, useState } from 'react';
import { getBiClientMargin } from 'interfaces/bi';
import { formatCurrency, formatPercent, marginRateBadge } from './helpers';

function ClientMarginTable(props: { from: string; to: string }) {
    const { from, to } = props;
    const [granularity, setGranularity] = useState<'month' | 'year'>('month');
    const [rows, setRows] = useState<any[]>([]);
    const [meta, setMeta] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(true);

    const fetchData = async (p = page, l = limit, g = granularity) => {
        setLoading(true);
        const res = await getBiClientMargin({ from, to, granularity: g, groupByClient: true, page: p, limit: l });
        setRows(res?.data ?? []);
        setMeta(res?.meta ?? null);
        setPage(p);
        setLimit(l);
        setLoading(false);
    };

    useEffect(() => {
        fetchData(1, limit, granularity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, granularity]);

    const displayName = (r: any) =>
        r.customerName || (r.customerKey === 'unknown' ? 'Client inconnu' : r.customerKey);

    return (
        <Card extra="p-5 border h-full">
            {/* Header */}
            <div className="mb-3 flex items-start justify-between">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">Marge par client</h4>
                    <p className="text-sm text-gray-500">Agrégée au mois ou à l'année</p>
                </div>
                <div className="flex gap-3 text-sm">
                    {(['month', 'year'] as const).map((g) => (
                        <button
                            key={g}
                            onClick={() => setGranularity(g)}
                            className={`cursor-pointer ${
                                granularity === g
                                    ? 'font-bold text-toola-500 underline'
                                    : 'text-gray-500'
                            }`}
                        >
                            {g === 'month' ? 'Mensuel' : 'Annuel'}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <p className="py-8 text-center text-sm text-gray-400">Chargement...</p>
            ) : rows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Aucune donnée sur la période.</p>
            ) : (
                <>
                    {/* ── Mobile cards ── */}
                    <div className="space-y-2 sm:hidden">
                        {rows.map((r) => (
                            <div
                                key={r.customerKey}
                                className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                            >
                                <p
                                    className="text-sm font-semibold text-navy-700"
                                    title={displayName(r)}
                                >
                                    {displayName(r)}
                                </p>
                                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                    <div>
                                        <p className="text-gray-400">CA HT</p>
                                        <p className="font-medium text-navy-700">
                                            {formatCurrency(r.revenueHT)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400">Marge</p>
                                        <p className="font-medium text-navy-700">
                                            {formatCurrency(r.marginAmount)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400">Cmd</p>
                                        <p className="font-medium text-navy-700">
                                            {r.ordersCount ?? '-'}
                                        </p>
                                    </div>
                                    <div className="col-span-3 mt-1">
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${marginRateBadge(r.marginRate)}`}
                                        >
                                            Tx marge {formatPercent(r.marginRate)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Desktop table ── */}
                    <div className="hidden sm:block">
                        <table className="w-full table-fixed">
                            <colgroup>
                                <col className="w-[38%]" />
                                <col className="w-[19%]" />
                                <col className="w-[19%]" />
                                <col className="w-[14%]" />
                                <col className="w-[10%]" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-gray-200 text-left">
                                    <th className="py-2 text-xs font-bold text-gray-600">Client</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">CA HT</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">Marge</th>
                                    <th className="py-2 text-center text-xs font-bold text-gray-600">Tx marge</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">Cmd</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.customerKey}
                                        className="border-b border-gray-100 hover:bg-gray-50"
                                    >
                                        <td className="overflow-hidden py-2 pr-3">
                                            <p
                                                className="truncate text-sm font-medium text-navy-700"
                                                title={displayName(r)}
                                            >
                                                {displayName(r)}
                                            </p>
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatCurrency(r.revenueHT)}
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatCurrency(r.marginAmount)}
                                        </td>
                                        <td className="py-2 text-center">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${marginRateBadge(r.marginRate)}`}
                                            >
                                                {formatPercent(r.marginRate)}
                                            </span>
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {r.ordersCount ?? '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <Pagination
                extra="mt-3"
                text={false}
                type="clients"
                maxItems={meta ? meta.total : 0}
                itemsPerPage={[10, 25, 50]}
                page={page}
                defaultItemsPerPage={limit}
                onChange={(p: number, l: number) => fetchData(p, l, granularity)}
            />
        </Card>
    );
}

export default ClientMarginTable;
